// Player model: creation, derived values (clout, net worth), and resource helpers.

import { TUNABLES } from "@degen/shared/config/tunables";
import type { GameState } from "./state";
import { priceOf } from "./market/token";
import type { AvatarIdentity } from "./avatar/traits";
import type { LaunchRecord } from "./market/playerLaunch";
import { nftValue } from "./nft/drops";
import { createTutorial, type TutorialState } from "./tutorial";
import { carsValue } from "./cars/cars";
import { housesValue } from "./houses/houses";
import { createGasState, dgnValue, type GasState } from "./gas";
import { starterPhone } from "./phones/phones";
import { perpsValueWusd } from "./perps/perps";

export type Origin = "novice" | "kol";
export type RarityTier = "Common" | "Uncommon" | "Rare" | "Epic" | "Legendary";

export interface PfpIdentity {
  seed: number;
  traits: Record<string, string>;
  rarity: RarityTier;
}

// SPEC-NOTE: token balances live in each token's holder ledger (single source of truth).
// A token holding here only tracks cost basis. NFT and airdrop-point holdings arrive in M6.
export interface TokenHolding {
  kind: "token";
  tokenId: string;
  costUsd: number; // total USD paid for the current position
}
export type Holding = TokenHolding;

export interface LifetimeStats {
  peakNetWorth: number;
  bestTradeUsd: number;
  worstTradeUsd: number;
  trades: number;
  coinsLaunched: number;
  collectionsLaunched: number;
  rugsPulled: number;
  rugsSurvived: number;
  posts: number;
  daysPlayed: number;
}

export interface DailyCounters {
  coffees: number;
  grass?: number; // touch-grass sessions today
  layLow: boolean;
  posts: number;
  postTypes: Record<string, number>; // posts per type today (diminishing returns, shill limit)
}

export interface Player {
  name: string;
  handle: string;
  bio: string;
  pfp: PfpIdentity;
  avatar: AvatarIdentity; // walking sprite: male / female / anon
  origin: Origin;
  cash: number; // WUSD
  dgn: number; // gas coin balance
  phone: string; // phone model you carry (changes the phone UI)
  phones: string[]; // phones you own
  gas: GasState;
  holdings: Holding[];
  followers: number;
  trust: number; // 0-100
  heat: number; // 0-100
  day: number;
  rank: number; // cached rank index, updated by the engine
  stats: LifetimeStats;
  daily: DailyCounters;
  launches: LaunchRecord[];
  tutorial: TutorialState;
  comebacks: number;
  flags: { launchpadBanUntilDay?: number; comebackUntilDay?: number };
}

export interface NewPlayerInput {
  name: string;
  handle: string;
  bio: string;
  pfp: PfpIdentity;
  avatar: AvatarIdentity;
  origin: Origin;
}

export function createPlayer(input: NewPlayerInput): Player {
  const o = TUNABLES.origins[input.origin];
  const bonus = TUNABLES.pfp.followerBonus[input.pfp.rarity] ?? 0;
  return {
    ...input,
    cash: o.cash,
    dgn: TUNABLES.gas.start,
    phone: starterPhone(input.origin === "kol"),
    phones: [starterPhone(input.origin === "kol")],
    gas: createGasState(),
    holdings: [],
    followers: Math.round(o.followers * (1 + bonus)),
    trust: o.trust,
    heat: TUNABLES.player.startHeat,
    day: 1,
    rank: 0,
    stats: {
      peakNetWorth: o.cash,
      bestTradeUsd: 0,
      worstTradeUsd: 0,
      trades: 0,
      coinsLaunched: 0,
      collectionsLaunched: 0,
      rugsPulled: 0,
      rugsSurvived: 0,
      posts: 0,
      daysPlayed: 0,
    },
    daily: { coffees: 0, layLow: false, posts: 0, postTypes: {} },
    launches: [],
    tutorial: createTutorial(input.origin),
    comebacks: 0,
    flags: {},
  };
}

export const clamp = (x: number, lo: number, hi: number) => (x < lo ? lo : x > hi ? hi : x);

export function playerClout(p: Player): number {
  const c = TUNABLES.clout;
  return p.followers * (c.base + c.trustWeight * (p.trust / 100));
}

/** Market value of the player's token bags (spot price; dead tokens are worth 0). */
export function portfolioValue(state: GameState): number {
  let v = 0;
  for (const h of state.player.holdings) {
    const t = state.market.tokens[h.tokenId];
    if (!t) continue;
    v += (t.holders["player"] ?? 0) * priceOf(t);
  }
  return v;
}

/** Cash + token bags at spot + NFTs at floor + cars (resale) + houses. Airdrop points count as 0. */
export function netWorth(state: GameState): number {
  return state.player.cash + dgnValue(state) + perpsValueWusd(state) + portfolioValue(state) + nftValue(state) + carsValue(state) + housesValue(state);
}

/** True when the player has any position in a live token. */
export function hasOpenPositions(state: GameState): boolean {
  return state.player.holdings.some((h) => {
    const t = state.market.tokens[h.tokenId];
    return !!t && t.phase !== "dead" && (t.holders["player"] ?? 0) > 0;
  });
}

export function addTrust(p: Player, d: number) {
  p.trust = clamp(p.trust + d, 0, 100);
}

export function addHeat(p: Player, d: number) {
  p.heat = clamp(p.heat + d, 0, 100);
}



const TIERS: RarityTier[] = ["Common", "Uncommon", "Rare", "Epic", "Legendary"];

/** "PFP flex" badge for Rare-or-better PFPs. */
export function hasPfpFlex(p: Player): boolean {
  return TIERS.indexOf(p.pfp.rarity) >= TIERS.indexOf(TUNABLES.pfp.flexBadgeFrom as RarityTier);
}
